import { useEffect, type RefObject } from 'react';

import { useAppStore } from '@/store/useAppStore';
import { logger } from '@/utils/logger';
import type { ProjectDataSubscriptionEvent } from '@/backend/contracts/project-data-client';
import { getEffectiveUserId } from '../../auth/current-user-identity';
import { mergeRolesWithDefaultPermissions } from '../../auth/permissions';
import {
    applyProjectMemberPersonnelLinks,
    loadProjectMemberContext,
    subscribeProjectData,
    type ProjectDataResourceKey,
} from '../project-reader';
import {
    buildRemoteProjectStoreUpdate,
    createLoadedResourcesForCloudBaseline,
    hasLoadedInitialCloudResources,
    shouldSkipPreloadedResourceSnapshot,
    type PendingGlobalCloudField,
} from '../project-remote-update';
import type { ProjectScope } from '../project-scope';
import { shouldApplyRemoteSnapshotForCloudAuthority } from '../project-autosave-coordinator';
import { hasSubscriptionResourcePayload } from '../subscription-control-fields';

type AppStateSnapshot = ReturnType<typeof useAppStore.getState>;
type CloudConnectionState = AppStateSnapshot['cloud'];

type SubscriptionPayloadState = {
    hasAuthoritativePersonnelPayload: boolean;
    hasLoadedResourcePayload: boolean;
    isUnsettledPersonnelPayload: boolean;
};

type ProjectSubscriptionEventInput = {
    applyCloudState: (updates: Partial<CloudConnectionState>) => void;
    event: ProjectDataSubscriptionEvent;
    projectScope: ProjectScope;
    onLoadedResourcesChange: (loadedResources: ReadonlySet<string>) => void;
    refs: Pick<
        ProjectReaderCoordinatorRefs,
        | 'hasInitialLoad'
        | 'isApplyingRemoteSnapshot'
        | 'isSaving'
        | 'loadedResources'
        | 'mountedRef'
        | 'pendingGlobalCloudFields'
        | 'preloadedResourceSnapshots'
        | 'remoteVersion'
        | 'timeoutRef'
    >;
    stateGetter: () => AppStateSnapshot;
};

export type ProjectReaderCoordinatorRefs = {
    activeSubscription: RefObject<(() => void) | null>;
    loadedResources: RefObject<Set<string>>;
    preloadedResourceSnapshots: RefObject<Set<string>>;
    hasInitialLoad: RefObject<boolean>;
    remoteVersion: RefObject<number>;
    isApplyingRemoteSnapshot: RefObject<boolean>;
    pendingGlobalCloudFields: RefObject<Set<PendingGlobalCloudField>>;
    pendingLocalAutosave: RefObject<boolean>;
    timeoutRef: RefObject<NodeJS.Timeout | null>;
    isSaving: RefObject<boolean>;
    mountedRef: RefObject<boolean>;
};

export type UseProjectReaderCoordinatorInput = {
    cloudEnabled: boolean;
    projectScope: ProjectScope | null;
    shouldSync: boolean;
    user: unknown;
    neededResources: readonly string[];
    hasLoadedCloudProject: boolean;
    authoritativeResourceIds?: readonly string[];
    stateGetter: () => AppStateSnapshot;
    applyCloudState: (updates: Partial<CloudConnectionState>) => void;
    onLoadedResourcesChange: (loadedResources: ReadonlySet<string>) => void;
    refs: ProjectReaderCoordinatorRefs;
};

function resetReaderRefs(refs: ProjectReaderCoordinatorRefs): void {
    refs.hasInitialLoad.current = false;
    refs.loadedResources.current = new Set();
    refs.preloadedResourceSnapshots.current = new Set();
    refs.pendingGlobalCloudFields.current = new Set();
    refs.pendingLocalAutosave.current = false;
}

function isCurrentProjectScope(
    stateGetter: () => AppStateSnapshot,
    projectScope: ProjectScope,
): boolean {
    const { cloud } = stateGetter();
    return cloud.orgId === projectScope.orgId && cloud.projectId === projectScope.projectId;
}

function getSubscriptionPayloadState(event: ProjectDataSubscriptionEvent): SubscriptionPayloadState {
    const hasResourcePayload = Boolean(event.resourceId && hasSubscriptionResourcePayload(event.update));
    const isUnsettledPersonnelPayload = event.resourceId === 'personnel'
        && hasResourcePayload
        && event.personnelSourcesSettled !== true;

    return {
        hasAuthoritativePersonnelPayload: event.personnelReadAuthoritative !== false,
        hasLoadedResourcePayload: Boolean(event.resourceId && hasResourcePayload && !isUnsettledPersonnelPayload),
        isUnsettledPersonnelPayload,
    };
}

function clearPersonnelReadAuthority(
    input: Pick<ProjectSubscriptionEventInput, 'applyCloudState' | 'stateGetter'>,
    updates: Partial<CloudConnectionState> = {},
): void {
    input.applyCloudState({
        ...input.stateGetter().cloud,
        hasLoadedPersonnel: false,
        ...updates,
    });
}

function handleUnavailablePersonnelPayload(
    input: ProjectSubscriptionEventInput,
    payloadState: SubscriptionPayloadState,
): boolean {
    const { event, refs } = input;
    if (event.resourceId !== 'personnel') {
        return false;
    }

    if (payloadState.isUnsettledPersonnelPayload) {
        refs.preloadedResourceSnapshots.current.delete(event.resourceId);
        clearPersonnelReadAuthority(input);
        return true;
    }

    if (payloadState.hasLoadedResourcePayload && !payloadState.hasAuthoritativePersonnelPayload) {
        refs.preloadedResourceSnapshots.current.delete(event.resourceId);
        refs.hasInitialLoad.current = hasLoadedInitialCloudResources(refs.loadedResources.current);
        clearPersonnelReadAuthority(input, { lastSync: new Date().toISOString() });
        return true;
    }

    return false;
}

function handleSkippedPreloadedResource(
    input: ProjectSubscriptionEventInput,
    payloadState: SubscriptionPayloadState,
): boolean {
    const { applyCloudState, event, refs, stateGetter } = input;
    if (!shouldSkipPreloadedResourceSnapshot(refs.preloadedResourceSnapshots.current, event.resourceId)) {
        return false;
    }

    if (event.resourceId === 'personnel' && payloadState.hasLoadedResourcePayload) {
        applyCloudState({
            ...stateGetter().cloud,
            hasLoadedPersonnel: payloadState.hasAuthoritativePersonnelPayload,
        });
    }
    return true;
}

function applyRemoteSubscriptionPayload(
    input: ProjectSubscriptionEventInput,
    payloadState: SubscriptionPayloadState,
): void {
    const { applyCloudState, event, projectScope, refs, stateGetter } = input;
    refs.remoteVersion.current++;
    refs.hasInitialLoad.current = hasLoadedInitialCloudResources(refs.loadedResources.current);
    const stateUpdate = buildRemoteProjectStoreUpdate({
        data: event.update,
        projectScope,
        stateGetter,
        pendingGlobalFields: refs.pendingGlobalCloudFields.current,
        applyCloudState,
    });

    refs.isApplyingRemoteSnapshot.current = true;
    useAppStore.setState(stateUpdate);
    queueMicrotask(() => {
        refs.isApplyingRemoteSnapshot.current = false;
    });

    applyCloudState({
        ...stateGetter().cloud,
        ...(event.resourceId === 'personnel' && payloadState.hasLoadedResourcePayload
            ? { hasLoadedPersonnel: payloadState.hasAuthoritativePersonnelPayload }
            : {}),
        lastSync: new Date().toISOString(),
    });
}

function handleProjectSubscriptionEvent(input: ProjectSubscriptionEventInput): void {
    const { event, projectScope, refs, stateGetter } = input;
    if (!refs.mountedRef.current) return;
    if (!isCurrentProjectScope(stateGetter, projectScope)) return;

    const payloadState = getSubscriptionPayloadState(event);
    if (event.resourceId && payloadState.hasLoadedResourcePayload) {
        const previouslyLoaded = refs.loadedResources.current.has(event.resourceId);
        refs.loadedResources.current.add(event.resourceId);
        if (!previouslyLoaded) {
            input.onLoadedResourcesChange(refs.loadedResources.current);
        }
    }

    if (handleUnavailablePersonnelPayload(input, payloadState)) return;
    if (handleSkippedPreloadedResource(input, payloadState)) return;

    if (!shouldApplyRemoteSnapshotForCloudAuthority({
        hasPendingAutosave: refs.timeoutRef.current !== null,
        isSaving: refs.isSaving.current,
    })) {
        return;
    }

    applyRemoteSubscriptionPayload(input, payloadState);
}

export function useProjectReaderCoordinator(input: UseProjectReaderCoordinatorInput): void {
    const {
        applyCloudState,
        cloudEnabled,
        hasLoadedCloudProject,
        neededResources,
        onLoadedResourcesChange,
        projectScope,
        authoritativeResourceIds,
        refs,
        shouldSync,
        stateGetter,
        user,
    } = input;
    const {
        activeSubscription: activeSubscriptionRef,
        hasInitialLoad: hasInitialLoadRef,
        isApplyingRemoteSnapshot: isApplyingRemoteSnapshotRef,
        isSaving: isSavingRef,
        loadedResources: loadedResourcesRef,
        mountedRef,
        pendingGlobalCloudFields: pendingGlobalCloudFieldsRef,
        preloadedResourceSnapshots: preloadedResourceSnapshotsRef,
        remoteVersion: remoteVersionRef,
        timeoutRef,
    } = refs;

    useEffect(() => {
        if (!cloudEnabled || !projectScope || !user) return;

        let didCancel = false;
        const loadMemberAndRoles = async () => {
            try {
                const effectiveUserId = getEffectiveUserId();
                if (!effectiveUserId) return;
                const { member, members: projectMembers, roles } = await loadProjectMemberContext(
                    projectScope,
                    effectiveUserId,
                );
                if (didCancel || !isCurrentProjectScope(stateGetter, projectScope)) return;

                useAppStore.getState().adminActions.setMembers(projectMembers);
                useAppStore.setState(state => ({
                    personnel: applyProjectMemberPersonnelLinks(state.personnel, projectMembers),
                }));
                useAppStore.getState().adminActions.setRoles(mergeRolesWithDefaultPermissions(roles));
                applyCloudState({
                    ...stateGetter().cloud,
                    member,
                    hasLoadedProjectAccess: true,
                });
            } catch (error: unknown) {
                logger.error('[useSync] Error loading member/roles:', error);
                if (!didCancel && projectScope && isCurrentProjectScope(stateGetter, projectScope)) {
                    applyCloudState({
                        ...stateGetter().cloud,
                        member: null,
                        hasLoadedProjectAccess: true,
                    });
                }
            }
        };

        void loadMemberAndRoles();
        return () => {
            didCancel = true;
        };
    }, [applyCloudState, cloudEnabled, projectScope, stateGetter, user]);

    useEffect(() => {
        if (activeSubscriptionRef.current) {
            activeSubscriptionRef.current();
            activeSubscriptionRef.current = null;
            resetReaderRefs(refs);
        }

        if (cloudEnabled && projectScope && shouldSync && user) {
            try {
                loadedResourcesRef.current = hasLoadedCloudProject
                    ? createLoadedResourcesForCloudBaseline(authoritativeResourceIds)
                    : new Set();
                onLoadedResourcesChange(loadedResourcesRef.current);
                preloadedResourceSnapshotsRef.current = hasLoadedCloudProject
                    ? createLoadedResourcesForCloudBaseline(authoritativeResourceIds)
                    : new Set();
                hasInitialLoadRef.current = hasLoadedInitialCloudResources(loadedResourcesRef.current);

                const unsubscribe = subscribeProjectData(
                    projectScope,
                    { resources: neededResources as readonly ProjectDataResourceKey[] },
                    event => {
                        handleProjectSubscriptionEvent({
                            applyCloudState,
                            event,
                            onLoadedResourcesChange,
                            projectScope,
                            refs: {
                                hasInitialLoad: hasInitialLoadRef,
                                isApplyingRemoteSnapshot: isApplyingRemoteSnapshotRef,
                                isSaving: isSavingRef,
                                loadedResources: loadedResourcesRef,
                                mountedRef,
                                pendingGlobalCloudFields: pendingGlobalCloudFieldsRef,
                                preloadedResourceSnapshots: preloadedResourceSnapshotsRef,
                                remoteVersion: remoteVersionRef,
                                timeoutRef,
                            },
                            stateGetter,
                        });
                    },
                );

                activeSubscriptionRef.current = unsubscribe;
            } catch (error: unknown) {
                logger.warn('[useSync] Subscription setup failed, falling back to local mode', error);
                onLoadedResourcesChange(new Set(neededResources));
            }
        }

        return () => {
            if (activeSubscriptionRef.current) {
                activeSubscriptionRef.current();
                activeSubscriptionRef.current = null;
                resetReaderRefs(refs);
            }
        };
    }, [
        activeSubscriptionRef,
        applyCloudState,
        cloudEnabled,
        authoritativeResourceIds,
        hasLoadedCloudProject,
        hasInitialLoadRef,
        isApplyingRemoteSnapshotRef,
        isSavingRef,
        neededResources,
        onLoadedResourcesChange,
        loadedResourcesRef,
        mountedRef,
        pendingGlobalCloudFieldsRef,
        projectScope,
        preloadedResourceSnapshotsRef,
        remoteVersionRef,
        refs,
        shouldSync,
        stateGetter,
        timeoutRef,
        user,
    ]);
}
